import { ApiFetchParams, ApiQueryOptions, useApiQuery } from "../helpers/useApi";

export type SalesInsightsGroupedAggregateFields = 'transactionVolume' | 'netSalesIncTax' | 'netSalesExcTax' | 'totalSalesIncTax' | 'totalSalesExcTax' | 'avgTransactionValue' | 'totalTax' | 'activeDevices' | 'activeStaff' | 'activeCustomers' | 'totalDiscount' | 'totalCustomerDiscount' | 'totalBasketDiscount' | 'totalItemDiscount' | 'totalQuantity' | 'totalServiceCharge' | 'totalContributions' | 'totalAmountPaidByPoints' | 'totalRefundAmount' | 'refundVolume' | 'totalRevenueExcTax';

export type Bucket = 'hour' | 'day' | 'week';

export type GetIndicatorsRequest = {
    fields?: string;
    startDateTime: string;
    startDate?: string;
    endDateTime: string;
    endDate?: string;
    locations?: string;
    locationArea?: string;
    compareStartDateTime?: string;
    compareEndDateTime?: string;
    bucket?: Bucket;
}

export type TopProductsCompareRequest = {
    fields: string;
    startDateTime: string;
    endDateTime: string;
    compareStartDateTime?: string;
    compareEndDateTime?: string;
    locations?: string;
    locationArea?: string;
    limit?: number;
}

export type GetSalesInsightsGroupedRequest = GetIndicatorsRequest & { groupBy: string };

export type GetTendersGroupedAggregateRequest = GetIndicatorsRequest & { businessStartHour: number, bucket: Bucket };

export type SalesInsights = {
    transactionVolume?: number;
    netSalesIncTax?: number;
    netSalesExcTax?: number;
    totalSalesIncTax?: number;
    totalSalesExcTax?: number;
    avgTransactionValue?: number;
    totalTax?: number;
    activeDevices?: number;
    activeStaff?: number;
    activeCustomers?: number;
    totalDiscount?: number;
    totalCustomerDiscount?: number;
    totalBasketDiscount?: number;
    totalItemDiscount?: number;
    totalQuantity?: number;
    totalServiceCharge?: number;
    totalContributions?: number;
    totalAmountPaidByPoints?: number;
    totalRefundAmount?: number;
    refundVolume?: number;
    totalRevenueExcTax?: number;
    refunds?: number;
    grossRevenue?: number;
    netRevenue?: number;
    costPriceExcTax?: number;
    grossMargin?: number;
    grossMarginRate?: number;
    netMargin?: number;
    netMarginRate?: number; 
}

export type SalesInsightsAggregateItem = {
    dateTime: string;
    salesInsights: SalesInsights;
}

type SalesInsightsGroupedItem = {
    key: string;
    salesInsights: SalesInsights;
}

export type SalesInsightsGroupedAggregateItem = {
    dateTime: string;
    salesInsightsGrouped: SalesInsightsGroupedItem[];
}

export type TendersGroupedAggregate = {
    tendersGrouped: [{
        key: string,
        fieldData: {
            amount: number,
            volume: number,
        }
    }]
}

export type TendersGroupedAggregateCompare = {
    currency?: string;
    tendersGroupedAggregate: [{
        dateTime: string,
        tendersGrouped: [{
            key: string,
            fieldData: {
                amount: number,
                volume: number,
            }
        }],
    }],
    comparedTendersGroupedAggregate: [{
        dateTime: string,
        tendersGrouped: [{
            key: string,
            fieldData: {
                amount: number,
                volume: number,
            }
        }]
    }]
}

export type GetSalesInsightsAggregatedRequest = {
    groupBy?: string;
    fields?: string;
    startDateTime?: string;
    endDateTime?: string;
    startDate?: string;
    endDate?: string;
    compareStartDateTime?: string;
    compareEndDateTime?: string;
    compareStartDate?: string;
    compareEndDate?: string;
    locations?: string;
    locationArea?: string;
    businessStartHour?: number;
    bucket?: Bucket;
}


export type GetTopProductRequest = {
    fields: string;
    startDateTime: string;
    endDateTime: string;
    locations?: string;
    locationArea?: string;
    limit?: number;
}

export type SalesInsightsCompareResponse = {
    currency?: string;
    salesInsights: SalesInsights;
    comparedSalesInsights: SalesInsights;
}

export type SalesInsightsResponse = {
    currency?: string;
    salesInsights: SalesInsights;
}

export type SalesInsightsGroupedResponse = {
    currency: string;
    salesInsightsGrouped: SalesInsightsGroupedItem[];
}

export type SalesInsightsGroupedAggregate = {
    currency?: string;
    salesInsightsGroupedAggregate: SalesInsightsGroupedAggregateItem[];
}

export type SalesInsightsComparedGroupedAggregate = {
    currency?: string;
    salesInsightsGroupedAggregate: SalesInsightsGroupedAggregateItem[];
    comparedSalesInsightsGroupedAggregate: SalesInsightsGroupedAggregateItem[];
}



export type SalesInsightsAggregate = {
    currency?: string;
    salesInsightsAggregate: SalesInsightsAggregateItem[];
}

export type SalesInsightsCompareAggregateResponse = {
    currency?: string;
    salesInsightsAggregate: SalesInsightsAggregateItem[];
    comparedSalesInsightsAggregate: SalesInsightsAggregateItem[];
}

export type SalesInsightsCompareGroupedResponse = {
    currency?: string;
    salesInsightsGrouped: SalesInsightsGroupedItem[];
    comparedSalesInsightsGrouped: SalesInsightsGroupedItem[];
}

export type TendersGroupedAggregateResponse = {
    currency: string,
    tendersGroupedAggregate: [{ dateTime: string } & TendersGroupedAggregate]
}

type Quantity = {
    productName: string;
    quantity: number;
}

type SaleValue = {
    productName: string;
    saleValue: number;
}

type GrossMargin = {
    productName: string;
    grossMargin: number;
}

type GrossMarginRate = {
    productName: string;
    grossMarginRate: number;
}

type NetMargin = {
    productName: string;
    netMargin: number;
}

type NetMarginRate = {
    productName: string;
    netMarginRate: number;
}

export type TopProductsResponse = {
    currency: string;
    topProducts: {
        quantity: Quantity[];
        saleValue: SaleValue[];
        grossMargin: GrossMargin[];
        grossMarginRate: GrossMarginRate[];
        netMargin: NetMargin[];
        netMarginRate: NetMarginRate[];
    }
}

export type TopProductsCompareResponse = {
    currency: string;
    topProducts: {
        quantity: Quantity[];
        saleValue: SaleValue[];
        grossMargin: GrossMargin[];
        grossMarginRate: GrossMarginRate[];
        netMargin: NetMargin[];
        netMarginRate: NetMarginRate[];
    }
    comparedTopProducts: {
        quantity: Quantity[];
        saleValue: SaleValue[];
        grossMargin: GrossMargin[];
        grossMarginRate: GrossMarginRate[];
        netMargin: NetMargin[];
        netMarginRate: NetMarginRate[];
    }
}


export function useSalesInsights<T>(params: GetIndicatorsRequest, queryKey: string, options?: ApiQueryOptions<T>) {
    return useApiQuery<T>({
        path: `/data/v1/indicators/sales-insights?${new URLSearchParams(JSON.parse(JSON.stringify(params)))}`,
        body: {},
        options
    } as ApiFetchParams, queryKey);
}

export function useSalesInsightsCompare<T>(params: GetSalesInsightsAggregatedRequest, queryKey: string) {
    return useApiQuery<T>({
        path: `/data/v1/indicators/sales-insights/compare?${new URLSearchParams(JSON.parse(JSON.stringify(params)))}`,
        body: {}
    } as ApiFetchParams, queryKey);
}

export function useSalesInsightsAggregated<T>(params: GetSalesInsightsAggregatedRequest, queryKey: string) {
    return useApiQuery<T>({
        path: `/data/v1/indicators/sales-insights/aggregate?${new URLSearchParams(JSON.parse(JSON.stringify(params)))}`,
        body: {}
    } as ApiFetchParams, queryKey);
}

export function useSalesInsightsCompareAggregated<T>(params: GetSalesInsightsAggregatedRequest, queryKey: string) {
    return useApiQuery<T>({
        path: `/data/v1/indicators/sales-insights/compare/aggregate?${new URLSearchParams(JSON.parse(JSON.stringify(params)))}`,
        body: {}
    } as ApiFetchParams, queryKey);
}

export function useSalesInsightsGrouped<T>(params: GetSalesInsightsGroupedRequest, queryKey: string) {
    return useApiQuery<T>({
        path: `/data/v1/indicators/sales-insights/grouped?${new URLSearchParams(JSON.parse(JSON.stringify(params)))}`,
        body: {}
    } as ApiFetchParams, queryKey);
}

export function useSalesInsightsCompareGrouped<T>(params: GetSalesInsightsGroupedRequest, queryKey: string) {
    return useApiQuery<T>({
        path: `/data/v1/indicators/sales-insights/compare/grouped?${new URLSearchParams(JSON.parse(JSON.stringify(params)))}`,
        body: {}
    } as ApiFetchParams, queryKey);
}

export function useSalesInsightsGroupedAggregated<T>(params: GetSalesInsightsAggregatedRequest, queryKey: string) {
    return useApiQuery<T>({
        path: `/data/v1/indicators/sales-insights/grouped-aggregate?${new URLSearchParams(JSON.parse(JSON.stringify(params)))}`,
        body: {}
    } as ApiFetchParams, queryKey);
}

export function useSalesInsightsCompareGroupedAggregated<T>(params: GetSalesInsightsAggregatedRequest, queryKey: string) {
    return useApiQuery<T>({
        path: `/data/v1/indicators/sales-insights/compare/grouped-aggregate?${new URLSearchParams(JSON.parse(JSON.stringify(params)))}`,
        body: {}
    } as ApiFetchParams, queryKey);
}

export function useTendersGroupedAggregate<T>(params: GetSalesInsightsAggregatedRequest, queryKey: string) {
    return useApiQuery<T>({
        path: `/data/v1/indicators/tenders/grouped-aggregate?${new URLSearchParams(JSON.parse(JSON.stringify(params)))}`,
        body: {}
    } as ApiFetchParams, queryKey);
}

export function useTendersGroupedAggregateCompare<T>(params: GetSalesInsightsAggregatedRequest, queryKey: string) {
    return useApiQuery<T>({
        path: `/data/v1/indicators/tenders/compare/grouped-aggregate?${new URLSearchParams(JSON.parse(JSON.stringify(params)))}`,
        body: {}
    } as ApiFetchParams, queryKey);
}


export function useTopProduct<T>(params: GetTopProductRequest, queryKey: string) {
    return useApiQuery<T>({
        path: `/data/v1/indicators/top-products?${new URLSearchParams(JSON.parse(JSON.stringify(params)))}`,
        body: {}
    } as ApiFetchParams, queryKey);
}

export function useTopComparedProduct<T>(params: TopProductsCompareRequest, queryKey: string) {
    return useApiQuery<T>({
        path: `/data/v1/indicators/top-products/compare?${new URLSearchParams(JSON.parse(JSON.stringify(params)))}`,
        body: {}
    } as ApiFetchParams, queryKey);
}

